import axios from 'axios';

// 生成请求 ID
function generateRequestId() {
  return `${Date.now()}-${Math.random().toString(36).substring(2)}`;
}

// 显示错误消息
function showError(message) {
  // 简单的错误提示，后续可以使用更好的UI组件
  console.error('API Error:', message);
  
  // 可以在这里添加 toast 或其他 UI 提示
  // 例如: toast.error(message);
  
  // 暂时使用 alert，后续替换为更好的UI
  // alert(message);
}

// 创建 Axios 实例
const request = axios.create({
  baseURL: '/index.php/api',
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
});

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    console.log('[Interceptor] Original config:', config);
    
    // 强制修改URL路径，移除旧的路径前缀
    if (config.url && config.url.includes('/guanli/public/index.php')) {
      console.log('[Interceptor] Removing old path prefix from:', config.url);
      config.url = config.url.replace('/guanli/public/index.php', '');
      console.log('[Interceptor] New url:', config.url);
    }
    
    // 确保URL以/开头
    if (config.url && !config.url.startsWith('/')) {
      config.url = '/' + config.url;
      console.log('[Interceptor] Added leading slash to url:', config.url);
    }
    
    // 添加 Token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 添加请求时间戳
    config.headers['X-Request-Time'] = Date.now();
    
    // 添加请求 ID（用于追踪）
    config.headers['X-Request-ID'] = generateRequestId();
    
    // 如果是 FormData，让浏览器自动设置 Content-Type（包含 boundary）
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    console.log(`[Request] ${config.method?.toUpperCase()} ${fullUrl}`, config.data || config.params);
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    let { data } = response;
    
    // 如果 data 是字符串，尝试解析为 JSON
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        console.error('Failed to parse response as JSON:', e);
      }
    }
    
    console.log(`[Response v2] ${response.config.url}`, data);
    console.log(`[Response v2] data.code:`, data.code);
    console.log(`[Response v2] data.data:`, data.data);
    
    // 统一处理响应数据
    if (data.code === 200 || data.code === 201) {
      return data;
    } else {
      // 业务错误处理
      const message = data.message || '请求失败';
      showError(message);
      return Promise.reject(new Error(message));
    }
  },
  async (error) => {
    // HTTP 错误处理
    if (error.response) {
      const { status, data } = error.response;
      
      console.error(`[Error] ${status}`, error.response.data);
      
      // 如果是404错误，并且URL包含旧的路径前缀，尝试重新请求
      if (status === 404 && error.config && error.config.url && error.config.url.includes('/guanli/public/index.php')) {
        console.log('[Retry] 检测到旧路径，尝试重新请求...');
        
        // 修改URL，移除旧的路径前缀
        const newUrl = error.config.url.replace('/guanli/public/index.php', '');
        const newConfig = { ...error.config, url: newUrl };
        
        try {
          // 重新请求
          const response = await request(newConfig);
          return response;
        } catch (retryError) {
          console.error('[Retry] 重新请求失败:', retryError);
          throw retryError;
        }
      }
      
      switch (status) {
        case 400:
          showError(data?.message || '请求参数错误');
          break;
          
        case 401:
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('token');
          if (window.location.pathname.startsWith('/user/profile') || window.location.pathname.startsWith('/user/edit')) {
            window.location.href = '/user/login';
          }
          break;
          
        case 403:
          showError('无权限访问');
          break;
          
        case 404:
          showError('请求的资源不存在');
          break;
          
        case 409:
          showError(data?.message || '资源冲突');
          break;
          
        case 422:
          showError(data?.message || '数据验证失败');
          break;
          
        case 429:
          showError('请求过于频繁，请稍后再试');
          break;
          
        case 500:
        case 502:
        case 503:
          showError('服务器错误，请稍后再试');
          break;
          
        default:
          showError(`请求失败 (${status})`);
      }
    } else if (error.request) {
      // 请求已发送但没有收到响应
      console.error('[Error] Network error');
      showError('网络错误，请检查网络连接');
    } else {
      // 其他错误
      console.error('[Error]', error);
      showError(error.message || '请求失败');
    }
    
    return Promise.reject(error);
  }
);

export default request;
